/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Small Ops Console parts shared by the new-transfer wizard and the item modals: the spinning
 * loader, the status tile (replaces the Coriolis status illustrations), selectable cards, section
 * titles and the arrow between a source item and its target mapping.
 */

import * as React from "react";
import styled, { css } from "styled-components";
import {
  ArrowRight,
  Check,
  CircleCheck,
  CircleHelp,
  CircleX,
  Clock,
  LoaderCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import IconTile from "@src/components/ui/IconTile";
import type { IconTileTone } from "@src/components/ui/IconTile/IconTile";
import { T9 } from "@src/components/ui/Lists/ListTable";

export { T9 };

// ------------------------------------------------------------------ loading

const spin = css`
  animation: t9-wizard-spin 1s linear infinite;
  @keyframes t9-wizard-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const SpinnerIcon = styled(LoaderCircle)`
  color: var(--t9-info-700);
  flex-shrink: 0;
  ${spin}
`;
/** The Ops loader: a spinning Lucide circle in the info blue. */
export const Spinner = ({ size = 24, style }: { size?: number; style?: React.CSSProperties }) => (
  <SpinnerIcon size={size} strokeWidth={2} style={style} data-test-id="wizardKit-spinner" />
);

const LoadingWrapper = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: ${props => (props.compact ? "24px 16px" : "48px 16px")};
  flex-grow: 1;
`;
const LoadingLabel = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${T9.slate500};
`;
/** A centred spinner with one line under it ("Loading instances…"). */
export const LoadingBlock = ({
  label,
  compact,
  style,
  testId,
}: {
  label?: React.ReactNode;
  compact?: boolean;
  style?: React.CSSProperties;
  testId?: string;
}) => (
  <LoadingWrapper compact={compact} style={style} data-test-id={testId}>
    <Spinner size={compact ? 24 : 32} />
    {label ? <LoadingLabel>{label}</LoadingLabel> : null}
  </LoadingWrapper>
);

// ------------------------------------------------------------ status tile

const SpinTile = styled.div`
  svg {
    ${spin}
  }
`;
const STATUS_TILES: { [status: string]: { icon: LucideIcon; tone: IconTileTone; spin?: boolean } } = {
  COMPLETED: { icon: CircleCheck, tone: "success" },
  SUCCESS: { icon: CircleCheck, tone: "success" },
  ERROR: { icon: CircleX, tone: "danger" },
  RUNNING: { icon: LoaderCircle, tone: "info", spin: true },
  QUESTION: { icon: CircleHelp, tone: "warning" },
  CONFIRMATION: { icon: CircleHelp, tone: "warning" },
  PENDING: { icon: Clock, tone: "neutral" },
};
/** The status illustration of a modal (validation result, confirmation…) as a 56 px icon tile. */
export const StatusTile = ({
  status,
  loading,
  size = 56,
}: {
  status?: string | null;
  loading?: boolean;
  size?: number;
}) => {
  const key = loading ? "RUNNING" : String(status || "PENDING").toUpperCase();
  const info = STATUS_TILES[key] || STATUS_TILES.PENDING;
  const tile = <IconTile icon={info.icon} size={size} tone={info.tone} />;
  return (
    <div data-test-id={`wizardKit-status-${key.toLowerCase()}`}>
      {info.spin ? <SpinTile>{tile}</SpinTile> : tile}
    </div>
  );
};

// -------------------------------------------------------------- headings

/** A section title inside a wizard step or modal (16 px semibold, Ops sentence case). */
export const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: ${T9.slate900};
  margin-bottom: 12px;
`;

/** The big header of an item modal: a 56 px icon tile, centred. */
export const ModalHeaderTile = styled.div`
  display: flex;
  justify-content: center;
  margin: 8px 0 24px 0;
`;

// ------------------------------------------------------------- mappings

const ArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  min-width: 32px;
  margin-right: 16px;
  color: ${T9.slate400};
`;
/** The arrow between a source item and its target dropdown (network, disk mapping). */
export const MappingArrow = () => (
  <ArrowWrapper aria-hidden>
    <ArrowRight size={18} strokeWidth={1.75} />
  </ArrowWrapper>
);

// ----------------------------------------------------------------- cards

/** An Ops card that can be picked: white, slate-200 hairline, brand when selected. */
export const ChoiceCard = styled.div<{ selected?: boolean; clickable?: boolean; muted?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-width: 0;
  background: var(--t9-bg-surface);
  border: 1px solid ${props => (props.selected ? T9.brand600 : T9.slate200)};
  box-shadow: ${props => (props.selected ? `0 0 0 1px ${T9.brand600}` : "none")};
  border-radius: 8px;
  transition:
    border-color 150ms ease,
    box-shadow 150ms ease,
    background 150ms ease;
  ${props =>
    props.clickable
      ? css`
          cursor: pointer;
        `
      : ""}
  &:hover {
    border-color: ${props => (props.selected ? T9.brand600 : T9.brand300)};
    box-shadow: ${props =>
      props.selected ? `0 0 0 1px ${T9.brand600}` : "0 2px 8px rgba(44, 62, 80, 0.08)"};
  }
  &:focus-visible {
    outline: 2px solid ${T9.accent500};
    outline-offset: 2px;
  }
`;

const CheckBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${T9.brand600};
  color: white;
`;
/** The tick in the corner of a selected card. */
export const SelectedCheck = () => (
  <CheckBadge aria-hidden data-test-id="wizardKit-selectedCheck">
    <Check size={13} strokeWidth={3} />
  </CheckBadge>
);
